/* eslint-disable react/style-prop-object */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Empty, Spin } from 'antd';
import Utils from '../../../../misc/Utils';
import DiagnosticReportUtils from '../../../../misc/DiagnosticReportUtils';
import { IntroIndSHS } from '../../diagnostic-components/IntroIndSHS';

function getAssessmentId(assignmentId, fallbackAssessmentId) {
   return assignmentId !== undefined ? assignmentId : fallbackAssessmentId;
}

function getDiagnosticReportUrl({ assignmentId, assessmentId, subjectId, studentId, template, schoolYear, preAssignmentId }, config) {
   const resolvedAssessmentId = getAssessmentId(assignmentId, assessmentId);
   const apiBase = process.env.REACT_APP_API_BRAINEE_DIAGNOSTIC;

   // if (config.apiVersion === 2) {
   //    return `${apiBase}/v2/getdiagnosticreport?s=${subjectId}&ai=${resolvedAssessmentId}&si=${studentId}&t=${template}&sy=${schoolYear}`;
   // }

   return `${apiBase}/v3/getdiagnosticreport?aid=${resolvedAssessmentId}&paid=${preAssignmentId}&si=${studentId}`;
}

export function SharedSHSIndividualReport({ config, ...props }) {
   const assignmentId = props.assignment_id;
   const assessmentId = props.assessment_id;
   const preAssignmentId = props.pre_assignment_id;
   const studentId = props.student_id;
   const subjectId = props.subject_id;
   const template = props.template;
   const schoolYear = props.school_year;
   const diagnosticType = props.diagnostic_type;

   const [reportData, setReportData] = useState(null);
   const [dataLoading, setDataLoading] = useState(false);

   useEffect(() => {
      Utils.verifyJwt();

      setDataLoading(true);
      axios.get(getDiagnosticReportUrl({ assignmentId, assessmentId, preAssignmentId, studentId, subjectId, template, schoolYear }, config)).then((response) => {
         if (response.data && response.data[0]) {
            const selectedData = response.data.find((item) => item.diagnostic_type === diagnosticType) || response.data[0];
            setReportData(selectedData);
         }
         setDataLoading(false);
      }, () => {
         setDataLoading(false);
      });
   }, [assignmentId, assessmentId, preAssignmentId, studentId, subjectId, template, schoolYear, diagnosticType, config]);

   const summaryContent = useMemo(() => {
      if (!reportData) return [];

      const areaRows = config.summaryAreas.map((area) => {
         const score = DiagnosticReportUtils.sumDataRange(reportData, area.count, area.start);
         return {
            label: area.label,
            data: score,
            count: area.count,
            val1: area.mean,
            val2: area.stdDev
         };
      });

      const totalCount = areaRows.reduce((sum, row) => sum + row.count, 0);
      const totalScore = areaRows.reduce((sum, row) => sum + row.data, 0);
      const totalMean = areaRows.reduce((sum, row) => sum + row.val1, 0);
      const totalStdDev = areaRows.reduce((sum, row) => sum + row.val2, 0);

      return [
         ...areaRows,
         {
            label: 'TOTAL',
            data: totalScore,
            count: totalCount,
            val1: totalMean,
            val2: totalStdDev
         }
      ];
   }, [config.summaryAreas, reportData]);

   const competenciesByIndex = useMemo(
      () => Object.fromEntries(config.competencies.map((item) => [item.index, item.label])),
      [config.competencies]
   );

   const subLabelsByStart = useMemo(
      () => Object.fromEntries((config.subLabels || []).map((item) => [item.start, item.label])),
      [config.subLabels]
   );

   if (dataLoading) {
      return (
         <div className='center-empty-space'>
            <Spin size='large' tip="Loading data, please wait..." />
         </div>
      );
   }

   if (reportData === null) {
      return (
         <div className='center-empty-space'>
            <Empty description='No Data Found' image="../images/brainee_mascot.png" />
         </div>
      );
   }

   return (
      <div className="dashboard-item-inner3" style={{ margin: '20px' }}>
         <div style={{ fontFamily: 'calibri' }} id='individual-report'>
            <IntroIndSHS
               subject={props.subject}
               report_data={reportData}
               summary_content={summaryContent}
            />

            <table cellSpacing={0} cellPadding={0} style={{ width: '100%', borderCollapse: 'collapse' }}>
               <tbody>
                  <tr>
                     <td style={{ width: '11.2pt', borderTopStyle: 'solid', borderTopWidth: '1pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                        <p style={{ marginTop: '0pt', marginBottom: '0pt', fontSize: '11pt' }}>
                           <strong><span style={{ color: '#ffffff' }}>&nbsp;</span></strong>
                        </p>
                     </td>
                     <td style={{ width: '330.2pt', borderTopStyle: 'solid', borderTopWidth: '1pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                        <p style={{ marginTop: '0pt', marginBottom: '0pt', fontSize: '11pt' }}>
                           <strong><span style={{ color: '#ffffff' }}>Learning Competency</span></strong>
                        </p>
                     </td>
                     <td style={{ width: '43.35pt', borderTopStyle: 'solid', borderTopWidth: '1pt', paddingRight: '5.4pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                        <p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}>
                           <strong><span style={{ color: '#ffffff' }}>Score</span></strong>
                        </p>
                     </td>
                     <td style={{ width: '75.35pt', borderTopStyle: 'solid', borderTopWidth: '1pt', borderRightStyle: 'solid', borderRightWidth: '1pt', paddingRight: '4.9pt', paddingLeft: '5.4pt', verticalAlign: 'middle', backgroundColor: '#000000' }}>
                        <p style={{ marginTop: '0pt', marginBottom: '0pt', textAlign: 'center', fontSize: '11pt' }}>
                           <strong><span style={{ color: '#ffffff' }}>Percent Correct for the Level</span></strong>
                        </p>
                     </td>
                  </tr>

                  {config.sections.map((section) => (
                     <React.Fragment key={section.label}>
                        {DiagnosticReportUtils.renderLabel(section.label)}
                        {Array.from({ length: section.end - section.start + 1 }, (_, offset) => {
                           const index = section.start + offset;
                           return (
                              <React.Fragment key={`ind-${index}`}>
                                 {subLabelsByStart[index] ? DiagnosticReportUtils.renderSubLabel(subLabelsByStart[index]) : null}
                                 {DiagnosticReportUtils.generatePercentCorrectForLevel(
                                    competenciesByIndex[index],
                                    reportData[`s${index}`],
                                    reportData[`percent_correct_s${index}`],
                                    index
                                 )}
                              </React.Fragment>
                           );
                        })}
                     </React.Fragment>
                  ))}
               </tbody>
            </table>
            <p style={{ marginTop: '0pt', marginBottom: '10pt' }}>
               <strong>&nbsp;</strong>
            </p>
            <p style={{ marginTop: '0pt', marginBottom: '10pt' }}>
               <strong>Name: </strong>{reportData.student_name}
            </p>
         </div>
      </div>
   );
}
